import { DateTime } from "luxon";
import { ADMIN_ROLES, type AdminRole } from "../../../utils/models";
import { ACTION_NAME_VALUES, CHANNEL_VALUES } from "./activityLogGroupedDisplay";

/**
 * The Grouped Activity Log's filter state lives in the URL, so a filtered log is a link someone can
 * share, and landing on that link opens the exact same view. Same model as the Team page
 * (`components/team/teamUrlState.ts`): an absent parameter means the page default.
 *
 * Multi-value filters are repeated parameters (`?performed_by=a@x.com&performed_by=b@x.com`) rather
 * than a comma list — usernames and free-text values could contain a comma.
 *
 * The state here holds primitives only (calendar-day strings, `HH:mm` strings, ids, booleans). The
 * hook turns them into department-zoned `DateTime`s behind `useMemo`, so typing in the search box —
 * also in the URL — never rebuilds the date objects the list query is keyed on.
 *
 * Dev Mode is deliberately NOT here: it is a support-only investigation aid, not part of what a
 * shared link should reproduce for the person who opens it.
 */
export const GROUPED_ACTIVITY_LOG_PARAM = {
	FROM: "from",
	TO: "to",
	PERFORMED_BY: "performed_by",
	PERFORMED_FOR: "performed_for",
	ROLE: "role",
	ACTION: "action",
	CHANNEL: "channel",
	SHIFT_DATE: "shift_date",
	SHIFT_START: "shift_start",
	SHIFT_END: "shift_end",
	SHIFT_JOB: "shift_job",
	EXACT_TIMES: "exact_times",
	PUNCHES: "punches",
	SEARCH: "q",
} as const;

/** Calendar-day format for `from`/`to`/`shift_date`, read in the department's timezone. */
export const GROUPED_ACTIVITY_LOG_DATE_FORMAT = "yyyy-MM-dd";
/** Time-of-day format for `shift_start`/`shift_end`. */
export const GROUPED_ACTIVITY_LOG_TIME_FORMAT = "HH:mm";

const ALL_PARAMS: readonly string[] = Object.values(GROUPED_ACTIVITY_LOG_PARAM);

/** Kept by "Clear all": the date range is the page's primary query axis and the search box is not a
 * rail filter — the same two things the pre-URL "Clear all" already left alone. */
const PARAMS_KEPT_ON_CLEAR: ReadonlySet<string> = new Set([GROUPED_ACTIVITY_LOG_PARAM.FROM, GROUPED_ACTIVITY_LOG_PARAM.TO, GROUPED_ACTIVITY_LOG_PARAM.SEARCH]);

const BOOLEAN_TRUE = "true";
const BOOLEAN_FALSE = "false";
const NURSE_ID_PATTERN = /^\d+$/;

const KNOWN_ROLES: ReadonlySet<string> = new Set(ADMIN_ROLES);
const KNOWN_ACTIONS: ReadonlySet<string> = new Set(ACTION_NAME_VALUES);
const KNOWN_CHANNELS: ReadonlySet<string> = new Set(CHANNEL_VALUES);

export type GroupedActivityLogUrlState = {
	/** `yyyy-MM-dd`, or null when absent/invalid (the hook then writes its default). */
	from: string | null;
	/** `yyyy-MM-dd`, inclusive — the hook reads it as the END of that day. */
	to: string | null;
	/** Raw usernames, or the "support"/"system" buckets a non-support caller filters by. */
	performedBy: string[];
	performedFor: number | null;
	adminRoles: AdminRole[];
	actionNames: string[];
	channels: string[];
	shiftDate: string | null;
	/** `HH:mm`. */
	shiftStartTime: string | null;
	/** `HH:mm`. */
	shiftEndTime: string | null;
	shiftJobIds: string[];
	exactShiftTimes: boolean;
	showPunches: boolean;
	search: string;
};

const isKnownRole = (value: string): value is AdminRole => KNOWN_ROLES.has(value);

/** A closed vocabulary: an unknown value (a stale link, a hand edit) is dropped, never sent — it could
 * only filter the log to zero rows with no chip or checkbox left to clear it. */
const readKnown = (params: URLSearchParams, key: string, known: ReadonlySet<string>): string[] => params.getAll(key).filter((value) => known.has(value));

const readFormatted = (params: URLSearchParams, key: string, format: string): string | null => {
	const value = params.get(key);
	if (value === null) return null;
	return DateTime.fromFormat(value, format).isValid ? value : null;
};

const readNurseId = (params: URLSearchParams): number | null => {
	const value = params.get(GROUPED_ACTIVITY_LOG_PARAM.PERFORMED_FOR);
	if (value === null || !NURSE_ID_PATTERN.test(value)) return null;
	return Number.parseInt(value, 10);
};

/** `true`/`false` are the only readable values; anything else is treated as absent. */
const readBoolean = (params: URLSearchParams, key: string): boolean | null => {
	const value = params.get(key);
	if (value === BOOLEAN_TRUE) return true;
	if (value === BOOLEAN_FALSE) return false;
	return null;
};

/**
 * `fallbackShowPunches` is the user's stored Punches preference. The URL wins when it says anything,
 * so a shared link shows the sender's view; a bare `/activity-log` keeps behaving as it always has.
 */
export function parseGroupedActivityLogUrlState(
	params: URLSearchParams,
	{ fallbackShowPunches }: { fallbackShowPunches: boolean },
): GroupedActivityLogUrlState {
	const urlShowPunches = readBoolean(params, GROUPED_ACTIVITY_LOG_PARAM.PUNCHES);
	return {
		from: readFormatted(params, GROUPED_ACTIVITY_LOG_PARAM.FROM, GROUPED_ACTIVITY_LOG_DATE_FORMAT),
		to: readFormatted(params, GROUPED_ACTIVITY_LOG_PARAM.TO, GROUPED_ACTIVITY_LOG_DATE_FORMAT),
		performedBy: params.getAll(GROUPED_ACTIVITY_LOG_PARAM.PERFORMED_BY).filter(Boolean),
		performedFor: readNurseId(params),
		adminRoles: params.getAll(GROUPED_ACTIVITY_LOG_PARAM.ROLE).filter(isKnownRole),
		actionNames: readKnown(params, GROUPED_ACTIVITY_LOG_PARAM.ACTION, KNOWN_ACTIONS),
		channels: readKnown(params, GROUPED_ACTIVITY_LOG_PARAM.CHANNEL, KNOWN_CHANNELS),
		shiftDate: readFormatted(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_DATE, GROUPED_ACTIVITY_LOG_DATE_FORMAT),
		shiftStartTime: readFormatted(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_START, GROUPED_ACTIVITY_LOG_TIME_FORMAT),
		shiftEndTime: readFormatted(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_END, GROUPED_ACTIVITY_LOG_TIME_FORMAT),
		shiftJobIds: params.getAll(GROUPED_ACTIVITY_LOG_PARAM.SHIFT_JOB).filter(Boolean),
		// DEFAULT: "Exact times" ships ON (the product ask is "the shift that starts and ends exactly
		// here"), so only its OFF state is ever written and anything else reads as ON.
		exactShiftTimes: readBoolean(params, GROUPED_ACTIVITY_LOG_PARAM.EXACT_TIMES) !== false,
		// DEFAULT: a URL without `punches` falls back to the user's stored preference — how the page
		// behaved before filters were in the URL. The page writes `punches` right after landing.
		showPunches: urlShowPunches === null ? fallbackShowPunches : urlShowPunches,
		// DEFAULT: an absent `q` is an empty search box — nothing typed, nothing filtered.
		search: params.get(GROUPED_ACTIVITY_LOG_PARAM.SEARCH) ?? "",
	};
}

const writeOptional = (params: URLSearchParams, key: string, value: string | null): void => {
	if (value === null) params.delete(key);
	else params.set(key, value);
};

const writeList = (params: URLSearchParams, key: string, values: readonly string[]): void => {
	params.delete(key);
	for (const value of values) params.append(key, value);
};

/**
 * Serializes the whole state onto `current`, leaving every unrelated parameter alone. `from`/`to` and
 * `punches` are always written (when known): the range is always applied, and Punches has a per-user
 * stored default, so a link that omitted it would open differently for whoever received it.
 */
export function writeGroupedActivityLogUrlState(current: URLSearchParams, state: GroupedActivityLogUrlState): URLSearchParams {
	const params = new URLSearchParams(current);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.FROM, state.from);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.TO, state.to);
	writeList(params, GROUPED_ACTIVITY_LOG_PARAM.PERFORMED_BY, state.performedBy);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.PERFORMED_FOR, state.performedFor === null ? null : String(state.performedFor));
	writeList(params, GROUPED_ACTIVITY_LOG_PARAM.ROLE, state.adminRoles);
	writeList(params, GROUPED_ACTIVITY_LOG_PARAM.ACTION, state.actionNames);
	writeList(params, GROUPED_ACTIVITY_LOG_PARAM.CHANNEL, state.channels);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_DATE, state.shiftDate);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_START, state.shiftStartTime);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_END, state.shiftEndTime);
	writeList(params, GROUPED_ACTIVITY_LOG_PARAM.SHIFT_JOB, state.shiftJobIds);
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.EXACT_TIMES, state.exactShiftTimes ? null : BOOLEAN_FALSE);
	params.set(GROUPED_ACTIVITY_LOG_PARAM.PUNCHES, state.showPunches ? BOOLEAN_TRUE : BOOLEAN_FALSE);
	// A whitespace-only query matches every row, so it is not worth a parameter.
	writeOptional(params, GROUPED_ACTIVITY_LOG_PARAM.SEARCH, state.search.trim().length > 0 ? state.search : null);
	return params;
}

/**
 * "Clear all": strips every rail filter but keeps the date range and the search box, and turns Punches
 * off explicitly (its stored preference is reset alongside by the caller, so the two agree).
 */
export function clearGroupedActivityLogFilters(current: URLSearchParams): URLSearchParams {
	const params = new URLSearchParams(current);
	for (const key of ALL_PARAMS) {
		if (!PARAMS_KEPT_ON_CLEAR.has(key)) params.delete(key);
	}
	params.set(GROUPED_ACTIVITY_LOG_PARAM.PUNCHES, BOOLEAN_FALSE);
	return params;
}

/** Which Grouped Activity Log parameters the URL carries (keys only — values can hold emails). */
export function groupedActivityLogUrlParamKeys(params: URLSearchParams): string[] {
	return ALL_PARAMS.filter((key) => params.has(key));
}

/** Whether the URL carries any Grouped Activity Log parameter — a shared grouped link. */
export function hasGroupedActivityLogUrlParams(params: URLSearchParams): boolean {
	return groupedActivityLogUrlParamKeys(params).length > 0;
}

/** Removes every Grouped Activity Log parameter, leaving unrelated ones — used when leaving for Raw. */
export function stripGroupedActivityLogUrlParams(current: URLSearchParams): URLSearchParams {
	const params = new URLSearchParams(current);
	for (const key of ALL_PARAMS) params.delete(key);
	return params;
}
